"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Compass, Home, Library, LogIn, Search, User, type LucideIcon } from "lucide-react";
import { cn } from "@gerama/utils";
import { useSiteAuthStore } from "@gerama/stores";
import { openLogin } from "@/features/auth/login-modal";
import { isActivePath } from "./shell/nav-items";

const ITEMS = [
  { href: "/", title: "خانه", icon: Home },
  { href: "/search", title: "جستجو", icon: Search },
  { href: "/browse", title: "کاوش", icon: Compass },
  { href: "/library", title: "موسیقی من", icon: Library },
] as const;

/**
 * Phone tab bar, shaped like a native app's: frosted glass over the content
 * scrolling under it, a hairline on top, safe-area aware, and the active tab
 * drawn in the brand colour with a heavier icon. Everything that doesn't fit
 * five tabs lives in the top bar's "more" sheet.
 */
export function MobileNav() {
  const pathname = usePathname();
  const user = useSiteAuthStore((s) => s.user);

  return (
    <nav
      className="bg-shell/95 supports-[backdrop-filter]:bg-shell/75 border-border/60 fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur-xl backdrop-saturate-150 md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      aria-label="ناوبری اصلی"
    >
      <div className="grid h-14 grid-cols-5">
        {ITEMS.map((item) => {
          const active = isActivePath(pathname, item.href);
          // Signed-out listeners have no library yet: the tab asks them to sign in instead.
          if (item.href === "/library" && !user) {
            return <Tab key={item.href} title={item.title} icon={item.icon} onClick={openLogin} />;
          }
          return (
            <Tab
              key={item.href}
              href={item.href}
              title={item.title}
              icon={item.icon}
              active={active}
            />
          );
        })}
        {user ? (
          <Tab href="/account" title="حساب" icon={User} active={pathname.startsWith("/account")} />
        ) : (
          <Tab title="ورود" icon={LogIn} onClick={openLogin} />
        )}
      </div>
    </nav>
  );
}

function Tab({
  href,
  title,
  icon: Icon,
  active = false,
  onClick,
}: {
  href?: string;
  title: string;
  icon: LucideIcon;
  active?: boolean;
  onClick?: () => void;
}) {
  const className = cn(
    "flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-[color,transform] select-none active:scale-90",
    active ? "text-primary" : "text-muted-foreground",
  );
  const body = (
    <>
      <Icon className="size-6" strokeWidth={active ? 2.4 : 1.8} />
      {title}
    </>
  );
  if (href) {
    return (
      <Link href={href} className={className} aria-current={active ? "page" : undefined}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {body}
    </button>
  );
}
